import { Link, useLocation } from 'react-router-dom';
import {
  TrophyIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  BeakerIcon,
  CloudIcon,
} from '@heroicons/react/24/outline';
import { Leaf } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Text } from '../ui/Text';

const adminNavigation = [
  {
    name: 'Badge Management',
    href: '/admin/badges',
    icon: TrophyIcon,
    description: 'Manage badge definitions and settings',
  },
  {
    name: 'Taxonomy Management',
    href: '/admin/taxonomy',
    icon: BeakerIcon,
    description: 'Manage botanical taxonomy and classification',
  },
  {
    name: 'Plant Varieties',
    href: '/admin/varieties',
    icon: Leaf,
    description: 'Manage plant varieties, cultivars, and common names',
  },
  {
    name: 'Site Harvest Admin',
    href: 'https://harvest.plantqr.net/admin',
    icon: CloudIcon,
    description: 'External site harvest administration panel',
    external: true,
  },
  // Future admin features
  {
    name: 'User Management',
    href: '/admin/users',
    icon: UsersIcon,
    description: 'Manage user accounts and permissions',
  },
  {
    name: 'Analytics',
    href: '/admin/analytics',
    icon: ChartBarIcon,
    description: 'View system analytics and reports',
    disabled: true,
  },
  {
    name: 'Settings',
    href: '/admin/settings',
    icon: CogIcon,
    description: 'System configuration and settings',
    disabled: true,
  },
];

export function AdminNavigation() {
  const location = useLocation();

  return (
    <nav className="space-y-1">
      {adminNavigation.map(item => {
        const isActive = location.pathname.startsWith(item.href);
        const Icon = item.icon;

        if (item.disabled) {
          return (
            <div
              key={item.name}
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md',
                'text-muted-foreground cursor-not-allowed opacity-50'
              )}
            >
              <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <Text size="sm" className="font-medium">
                  {item.name}
                </Text>
                <Text size="xs" variant="muted" className="hidden md:block">
                  Coming soon
                </Text>
              </div>
            </div>
          );
        }

        // Handle external links
        if (item.external) {
          return (
            <a
              key={item.name}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                'text-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {/* Handle both Heroicons and Lucide icons */}
              {Icon === Leaf ? (
                <Leaf className="h-5 w-5 mr-3 flex-shrink-0" />
              ) : (
                <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <Text size="sm" className="font-medium">
                  {item.name}
                </Text>
                <Text size="xs" variant="muted" className="hidden md:block">
                  {item.description}
                </Text>
              </div>
            </a>
          );
        }

        return (
          <Link
            key={item.name}
            to={item.href}
            className={cn(
              'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            {/* Handle both Heroicons and Lucide icons */}
            {Icon === Leaf ? (
              <Leaf className="h-5 w-5 mr-3 flex-shrink-0" />
            ) : (
              <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <Text size="sm" className="font-medium">
                {item.name}
              </Text>
              <Text size="xs" variant="muted" className="hidden md:block">
                {item.description}
              </Text>
            </div>
          </Link>
        );
      })}
    </nav>
  );
}
